import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, Toggle, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { PhotographerAvailability, AvailabilityStatus } from "../../../store/types";

const NOW = () => new Date().toISOString();

const AVAIL_STATUSES: AvailabilityStatus[] = ["available", "booked", "blocked"];

const STATUS_STYLES: Record<AvailabilityStatus, string> = {
  available: "bg-emerald-50 text-emerald-600",
  booked: "bg-[#C8B8A6]/20 text-[#C8B8A6]",
  blocked: "bg-[#D9D9D9]/50 text-[#2C2C2C]/40",
};

function emptyAvailability(): PhotographerAvailability {
  return {
    id: `avail-${Date.now()}`,
    photographer_id: "",
    available_date: new Date().toISOString().slice(0, 10),
    available_start_time: "09:00",
    available_end_time: "18:00",
    city: "",
    status: "available",
    note: "",
    created_at: NOW(),
    updated_at: NOW(),
  };
}

function EditModal({
  form, onChange, onClose, onSave, saving, isNew,
  photographerOptions,
}: {
  form: PhotographerAvailability; onChange: (a: PhotographerAvailability) => void;
  onClose: () => void; onSave: () => void; saving: boolean; isNew: boolean;
  photographerOptions: Array<{ id: string; name: string }>;
}) {
  const t = useAdminT();
  const set = (field: keyof PhotographerAvailability, value: unknown) => onChange({ ...form, [field]: value });

  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.photographer_availability.add_availability") : t("tabifotoAdmin.photographer_availability.edit_availability")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <FormField label={t("tabifotoAdmin.photographer_availability.photographer_id")} required>
        <select className={inputCls} value={form.photographer_id} onChange={(e) => set("photographer_id", e.target.value)}>
          <option value="">—</option>
          {photographerOptions.map((ph) => <option key={ph.id} value={ph.id}>{ph.name}</option>)}
        </select>
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographer_availability.available_date")} required>
          <input className={inputCls} type="date" value={form.available_date} onChange={(e) => set("available_date", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographer_availability.city")} required>
          <input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Kyoto" />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographer_availability.available_start_time")} required>
          <input className={inputCls} type="time" value={form.available_start_time} onChange={(e) => set("available_start_time", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographer_availability.available_end_time")} required>
          <input className={inputCls} type="time" value={form.available_end_time} onChange={(e) => set("available_end_time", e.target.value)} />
        </FormField>
      </div>

      <FormField label={t("tabifotoAdmin.photographer_availability.status")} required>
        <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value as AvailabilityStatus)}>
          {AVAIL_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`tabifotoAdmin.photographer_availability.status_${s}`)}</option>
          ))}
        </select>
      </FormField>

      <FormField label={t("tabifotoAdmin.photographer_availability.note")}>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.note} onChange={(e) => set("note", e.target.value)} maxLength={500} />
      </FormField>
    </AdminModal>
  );
}

export default function AdminPhotographerAvailabilityPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertAvailability, removeAvailability, refetchAvailability } = useAdminData();
  const toast = useToast();

  const [editItem, setEditItem] = useState<PhotographerAvailability | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<AvailabilityStatus | "all">("all");
  const [filterCity, setFilterCity] = useState("");

  const photographerOptions = state.photographers
    .filter((p) => p.is_active)
    .map((p) => ({ id: p.id, name: p.display_name }));

  const getPhotographerName = (id: string) =>
    state.photographers.find((p) => p.id === id)?.display_name ?? id;

  const cities = [...new Set(state.photographerAvailability.map((a) => a.city).filter(Boolean))].sort();

  const filtered = [...state.photographerAvailability]
    .filter((a) => {
      if (filterStatus !== "all" && a.status !== filterStatus) return false;
      if (filterCity && a.city !== filterCity) return false;
      return true;
    })
    .sort((a, b) => a.available_date < b.available_date ? 1 : -1);

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertAvailability(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeAvailability(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  const FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.photographer_availability.filter_all") },
    { key: "available" as const, label: t("tabifotoAdmin.photographer_availability.status_available") },
    { key: "booked" as const, label: t("tabifotoAdmin.photographer_availability.status_booked") },
    { key: "blocked" as const, label: t("tabifotoAdmin.photographer_availability.status_blocked") },
  ];

  const headers = [
    t("tabifotoAdmin.photographer_availability.col_photographer"),
    t("tabifotoAdmin.photographer_availability.col_date"),
    t("tabifotoAdmin.photographer_availability.col_time"),
    t("tabifotoAdmin.photographer_availability.col_city"),
    t("tabifotoAdmin.photographer_availability.col_status"),
    t("tabifotoAdmin.photographer_availability.col_note"),
    t("tabifotoAdmin.photographer_availability.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.photographer_availability.title")}
      subtitle={t("tabifotoAdmin.photographer_availability.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyAvailability())}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.photographer_availability.add_availability")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterStatus === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
        {cities.length > 0 && (
          <select className="px-3 py-1.5 rounded-lg text-xs border border-[#D9D9D9] bg-white text-[#2C2C2C]/60 focus:outline-none focus:border-[#C8B8A6]"
            value={filterCity} onChange={(e) => setFilterCity(e.target.value)}>
            <option value="">— {t("tabifotoAdmin.photographer_availability.col_city")} —</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {status.availability.loading ? <LoadingState /> :
        status.availability.error ? <ErrorState message={status.availability.error} onRetry={refetchAvailability} /> : (
          <AdminTable headers={headers} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.photographer_availability.no_availability")}>
            {filtered.map((a) => (
              <tr key={a.id} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors">
                <td className="px-5 py-3 text-sm font-light text-[#2C2C2C]">{getPhotographerName(a.photographer_id)}</td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">{a.available_date}</td>
                <td className="px-5 py-3">
                  <span className="text-xs text-[#2C2C2C]/60 font-mono">{a.available_start_time} – {a.available_end_time}</span>
                </td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">{a.city || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${STATUS_STYLES[a.status]}`}>
                    {t(`tabifotoAdmin.photographer_availability.status_${a.status}`)}
                  </span>
                </td>
                <td className="px-5 py-3 max-w-[200px]">
                  {a.note ? <p className="text-xs text-[#2C2C2C]/50 line-clamp-2">{a.note}</p> : <span className="text-xs text-[#2C2C2C]/20">—</span>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditItem({ ...a })}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                      <i className="ri-edit-line text-base" />
                    </button>
                    <button onClick={() => setDeleteId(a.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.photographerAvailability.some((a) => a.id === editItem.id)}
          photographerOptions={photographerOptions} />
      )}

      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.photographer_availability.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
